import { networkIconFor } from '@alison/ui';
import ArticleOutlined from '@mui/icons-material/ArticleOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { alpha } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { ELLIPSIS_SX } from '../creatives/utils.js';
import { iconFor } from '../workflows/workflowIcons.js';
import { BriefSourceIcon, KIND_ICON, SOURCE_ICON } from './OutcomeIcons.js';
import type { Outcome } from './outcomes.js';

const COLUMNS =
  'minmax(0, 2.2fr) minmax(0, 1.1fr) minmax(0, 1.4fr) minmax(0, 1fr) minmax(0, 0.9fr) minmax(0, 0.9fr) 84px';

const HEADINGS = ['Name', 'Type', 'Workflow', 'Created by', 'Date', 'Platform'];

const FRAME_SX = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1.5,
  overflow: 'hidden',
  bgcolor: 'background.paper',
};

const HEAD_SX = {
  display: 'grid',
  gridTemplateColumns: COLUMNS,
  gap: 2,
  py: 2,
  px: 3,
  bgcolor: 'background.page',
  borderBottom: '1px solid',
  borderColor: 'divider',
  fontSize: 10,
  fontWeight: 700,
  textTransform: 'uppercase',
  color: 'text.disabled',
};

const ROW_SX = {
  display: 'grid',
  gridTemplateColumns: COLUMNS,
  gap: 2,
  px: 3,
  minHeight: 68,
  alignItems: 'center',
  cursor: 'pointer',
  borderBottom: '1px solid',
  borderColor: 'divider',
  transition: 'background-color .15s ease',
  '&:last-of-type': { borderBottom: 0 },
  '&:hover': { bgcolor: 'action.hover' },
  '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: -2 },
};

const GLYPH_SX = {
  width: 28,
  height: 28,
  borderRadius: 1,
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

const LINK_SX = { color: 'primary.main', ...ELLIPSIS_SX };

const SOURCE_SX = {
  p: 0.25,
  border: 0,
  borderRadius: 0.5,
  cursor: 'pointer',
  color: 'text.muted',
  bgcolor: 'transparent',
  display: 'inline-flex',
  flexShrink: 0,
  transition: 'color .15s ease',
  '&:hover': { color: 'primary.main' },
  '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' },
};

export interface OutcomeRow {
  outcome: Outcome;
  platforms: string[];
  when: string;
}

export interface OutcomesTableProps {
  rows: OutcomeRow[];
  onOpen: (outcome: Outcome) => void;
  onOpenSource: (outcome: Outcome, source: Outcome['sources'][number]) => void;
}

function typeLabel(kind: string): string {
  return kind.charAt(0).toUpperCase() + kind.slice(1);
}

export function OutcomesTable({ rows, onOpen, onOpenSource }: OutcomesTableProps): ReactNode {
  return (
    <Box data-testid="table-outcomes" sx={FRAME_SX}>
      <Box sx={HEAD_SX}>
        {HEADINGS.map((heading) => (
          <Box key={heading} component="span">
            {heading}
          </Box>
        ))}
        <Box component="span" sx={{ textAlign: 'center' }}>
          Actions
        </Box>
      </Box>

      {rows.map(({ outcome, platforms, when }) => {
        const Glyph = iconFor(outcome.icon ?? '', KIND_ICON[outcome.kind] ?? ArticleOutlined);
        return (
          <Box
            key={`${outcome.runId}|${outcome.kind}`}
            data-testid={`outcome-${outcome.runId}-${outcome.kind}`}
            role="button"
            tabIndex={0}
            aria-label={outcome.title}
            onClick={() => onOpen(outcome)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onOpen(outcome);
              }
            }}
            sx={ROW_SX}
          >
            <Stack direction="row" sx={{ gap: 1.5, alignItems: 'center', minWidth: 0 }}>
              <Stack
                sx={{ ...GLYPH_SX, bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1) }}
              >
                <Glyph sx={{ fontSize: 16, color: 'primary.main' }} />
              </Stack>
              <Tooltip placement="top-start" title={outcome.title}>
                <Typography sx={{ fontWeight: 700, ...ELLIPSIS_SX }}>{outcome.title}</Typography>
              </Tooltip>
            </Stack>

            <Stack direction="row" sx={{ gap: 0.5, alignItems: 'center', minWidth: 0 }}>
              <Typography sx={LINK_SX}>{typeLabel(outcome.kind)}</Typography>
              {outcome.sources.map((source) => {
                const SourceGlyph = SOURCE_ICON[source.kind] ?? BriefSourceIcon;
                return (
                  <Tooltip key={source.runId} title={`Open the ${source.kind} this came from`}>
                    <Box
                      component="button"
                      type="button"
                      aria-label={`Open the ${source.kind} this was built from`}
                      onClick={(event) => {
                        event.stopPropagation();
                        onOpenSource(outcome, source);
                      }}
                      sx={SOURCE_SX}
                    >
                      <SourceGlyph sx={{ fontSize: 13 }} />
                    </Box>
                  </Tooltip>
                );
              })}
            </Stack>

            <Typography sx={LINK_SX}>{outcome.workflow}</Typography>
            <Typography sx={ELLIPSIS_SX}>{outcome.creator ?? '—'}</Typography>
            <Typography sx={ELLIPSIS_SX}>{when}</Typography>

            <Stack direction="row" sx={{ gap: 0.5, alignItems: 'center' }}>
              {platforms.length === 0 ? <Typography>—</Typography> : null}
              {platforms.map((slug) => {
                const Icon = networkIconFor(slug);
                return Icon === undefined ? null : (
                  <Box key={slug} sx={{ display: 'inline-flex' }}>
                    <Icon titleAccess={slug} sx={{ fontSize: 16 }} />
                  </Box>
                );
              })}
            </Stack>

            <Box sx={{ textAlign: 'center' }}>
              <Button
                size="small"
                variant="outlined"
                onClick={(event) => {
                  event.stopPropagation();
                  onOpen(outcome);
                }}
              >
                View
              </Button>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
